/*
    설정 전체 검색 — types.ts 의 keywords 필드("Search keywords for future search
    feature")를 실제로 소비하는 첫 구현.

    데이터 주도 설정 페이지(화면·테마 / 접근성 / 고급 / 언어 / AI 모델 파라미터 /
    모델 프리셋 옵션 / 인레이)를 현재 언어 라벨 + keywords + 도움말 본문으로 매칭하고,
    결과에 "탭 > 서브탭" 브레드크럼을 붙여 클릭 시 해당 위치로 이동시킨다.
    코드로 직접 작성된 페이지(시스템·플러그인 등)는 대상 밖 — 라벨이 데이터에 없다.
*/
import { writable } from 'svelte/store';
import type { SettingItem } from './types';
import { language } from 'src/lang';
import { getLabel } from './utils';
import {
    displayThemeSettingsItems,
    displaySizeSettingsItems,
    displayOtherSettingsItems,
} from './displaySettingsData.svelte';
import {
    accessibilityEditingItems,
    accessibilityScrollItems,
    accessibilitySidebarItems,
    accessibilityOtherItems,
} from './accessibilitySettingsData';
import { advancedSettingsItems } from './advancedSettingsData';
import { languageSettingsItems } from './languageSettingsData.svelte';
import { allBasicParameterItems } from './botSettingsParamsData';
import { modelPresetOptionsItems } from './modelPresetOptionsData';
import { inlayImageSettingsItems } from './inlayImageSettingsData';

/**
 * 검색 결과 클릭 시 대상 페이지가 열어야 할 서브탭(submenu 숫자).
 * menu 를 함께 담아 "지금 열려 있는 다른 페이지"가 점프를 가로채지 못하게 한다 —
 * 각 페이지는 자기 menuIndex 와 일치할 때만 consume 후 null 로 되돌린다.
 * (접근성은 전역 AccessibilitySubmenuIndex 를 직접 세팅하므로 이 스토어를 쓰지 않는다.)
 */
export const settingsJumpTab = writable<{ menu: number; tab: number } | null>(null);

export interface SettingsSearchResult {
    /** 현재 언어로 해석된 설정 라벨 */
    label: string;
    /** "화면·테마 > 기타" 형태의 위치 안내 */
    breadcrumb: string;
    menuIndex: number;
    jumpTab: number | null;
}

type Source = {
    items: SettingItem[];
    menuIndex: number;
    menuLabel: () => string;
    tabLabel?: () => string;
    jumpTab?: number;
};

function sources(): Source[] {
    const L = language as any;
    return [
        { items: displayThemeSettingsItems, menuIndex: 3, menuLabel: () => L.display, tabLabel: () => L.theme, jumpTab: 0 },
        { items: displaySizeSettingsItems, menuIndex: 3, menuLabel: () => L.display, tabLabel: () => L.sizeAndSpeed, jumpTab: 1 },
        { items: displayOtherSettingsItems, menuIndex: 3, menuLabel: () => L.display, tabLabel: () => L.others, jumpTab: 2 },
        { items: accessibilityEditingItems, menuIndex: 11, menuLabel: () => L.accessibility, tabLabel: () => L.accTabEditing, jumpTab: 0 },
        { items: accessibilityScrollItems, menuIndex: 11, menuLabel: () => L.accessibility, tabLabel: () => L.accTabScroll, jumpTab: 1 },
        { items: accessibilitySidebarItems, menuIndex: 11, menuLabel: () => L.accessibility, tabLabel: () => L.accTabSidebar, jumpTab: 2 },
        { items: accessibilityOtherItems, menuIndex: 11, menuLabel: () => L.accessibility, tabLabel: () => L.others, jumpTab: 3 },
        { items: advancedSettingsItems, menuIndex: 6, menuLabel: () => L.advancedSettings },
        { items: languageSettingsItems, menuIndex: 10, menuLabel: () => L.language },
        { items: allBasicParameterItems, menuIndex: 1, menuLabel: () => L.chatBot, tabLabel: () => L.parameters, jumpTab: 1 },
        { items: modelPresetOptionsItems, menuIndex: 18, menuLabel: () => L.modelPresetMenu },
        { items: inlayImageSettingsItems, menuIndex: 23, menuLabel: () => L.inlayImageGallery ?? L.playground?.inlayImageGallery ?? 'Inlay' },
    ];
}

/*
    페이지 단위 검색 항목 — 코드로 직접 작성돼 데이터 항목이 없는 페이지
    (시스템·백업·플러그인·모듈·원격·데이터 이전)와, 메뉴 버튼이 없어 도달 불가였던
    전역 정규식/전역 로어북을 검색으로 찾게 한다. 메뉴를 늘리지 않고 발견성만 회복.
*/
interface PageEntry {
    labelKey: string;
    menuIndex: number;
    keywords: string[];
}
const PAGE_ENTRIES: PageEntry[] = [
    { labelKey: 'globalRegexScript', menuIndex: 9, keywords: ['regex', 'script', 'global', '정규식', '스크립트', '전역'] },
    { labelKey: 'globalLoreBook', menuIndex: 8, keywords: ['lorebook', 'lore', 'global', '로어북', '전역', '세계관'] },
    { labelKey: 'system', menuIndex: 22, keywords: ['system', 'dashboard', 'logs', 'diagnostics', '시스템', '로그', '진단'] },
    { labelKey: 'systemBackups', menuIndex: 22, keywords: ['backup', 'restore', 'snapshot', '백업', '복원', '스냅샷'] },
    { labelKey: 'plugin', menuIndex: 4, keywords: ['plugin', 'extension', '플러그인', '확장'] },
    { labelKey: 'modules', menuIndex: 14, keywords: ['module', '모듈'] },
    { labelKey: 'remoteAccess', menuIndex: 21, keywords: ['remote', 'access', 'password', '원격', '접속', '비밀번호'] },
    { labelKey: 'migration', menuIndex: 0, keywords: ['migration', 'import', 'export', 'transfer', '이전', '가져오기', '내보내기', '데이터'] },
    { labelKey: 'hotkey', menuIndex: 15, keywords: ['hotkey', 'shortcut', 'keyboard', '단축키', '키보드'] },
];

function helpText(item: SettingItem): string {
    if (!item.helpKey) return '';
    const h = (language as any).help?.[item.helpKey];
    return typeof h === 'string' ? h : '';
}

/**
 * 라벨 일치 > keywords 일치 > 도움말 본문 일치 순으로 정렬해 최대 30건 반환.
 * header/라벨 없는 항목은 제외(빈 라벨 결과 방지).
 */
export function searchSettings(query: string): SettingsSearchResult[] {
    const q = query.trim().toLowerCase();
    if (q.length < 1) return [];

    const scored: (SettingsSearchResult & { score: number })[] = [];

    // 페이지 단위 항목 먼저 — 페이지 이름/키워드 일치 시 상단에 노출(항목보다 큰 단위).
    for (const pe of PAGE_ENTRIES) {
        const label = (language as any)[pe.labelKey];
        if (typeof label !== 'string' || !label) continue;
        let score = 0;
        if (label.toLowerCase().includes(q)) score = 4;
        else if (pe.keywords.some(k => k.toLowerCase().includes(q))) score = 2;
        if (score === 0) continue;
        scored.push({ label, breadcrumb: (language as any).settingsSearchPageBadge ?? 'Page', menuIndex: pe.menuIndex, jumpTab: null, score });
    }

    for (const src of sources()) {
        for (const item of src.items) {
            if (item.type === 'header') continue;
            const label = getLabel(item);
            if (!label) continue;

            let score = 0;
            if (label.toLowerCase().includes(q)) score = 3;
            else if ((item.keywords ?? []).some(k => k.toLowerCase().includes(q))) score = 2;
            else if (helpText(item).toLowerCase().includes(q)) score = 1;
            if (score === 0) continue;

            const menuLabel = src.menuLabel() ?? '';
            const tabLabel = src.tabLabel?.() ?? '';
            scored.push({
                label,
                breadcrumb: tabLabel ? `${menuLabel} > ${tabLabel}` : menuLabel,
                menuIndex: src.menuIndex,
                jumpTab: src.jumpTab ?? null,
                score,
            });
        }
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 30).map(({ score: _score, ...r }) => r);
}
